import { ProductForm } from '@up2stay/up2promo-api-client';
import { cn, ghostAccentIconTileClass } from '@up2stay/up2promo-ui';
import { Package } from 'lucide-react';

/**
 * Physical-form tiles — form color is intentional product identity
 * (stand/card/badge differentiation), not page-local brand chrome.
 * Unknown form falls back to shared ghost-accent tile.
 */

const STAND_PATH =
  'M78.7,9.4 L77.0,9.0 L32.8,11.9 L31.5,13.0 L19.9,84.9 L20.0,87.0 L20.8,88.1 L72.8,90.9 L72.6,87.4 L73.5,76.9 L74.5,75.1 L80.0,11.6 Z';

const CARD_PATH =
  'M76.8,17.7 L75.3,17.0 L73.5,17.0 L68.7,18.6 L32.3,33.0 L12.8,40.2 L9.5,42.2 L9.0,44.9 L14.8,63.4 L18.8,74.2 L20.0,79.0 L21.5,82.1 L23.4,83.0 L27.3,82.1 L88.4,59.5 L90.3,58.1 L91.0,56.8 L90.8,54.0 L78.2,20.3 Z';

const BADGE_PATH =
  'M49.8,58.5 L49.1,59.1 L46.4,59.4 L42.6,60.8 L40.3,62.2 L38.3,64.0 L36.5,66.4 L34.9,69.6 L34.2,72.7 L34.1,76.1 L34.5,78.8 L35.4,81.3 L37.1,84.3 L39.5,86.9 L41.5,88.4 L44.6,90.0 L47.4,90.7 L50.8,91.0 L53.9,90.6 L57.1,89.4 L59.9,87.7 L62.3,85.3 L64.1,82.8 L65.4,79.5 L65.9,76.7 L65.9,73.7 L65.5,71.2 L64.7,68.9 L63.5,66.7 L61.1,63.7 L58.8,61.7 L56.4,60.4 L53.7,59.5 L50.6,59.1 Z M46.5,9.0 L44.7,9.6 L43.6,10.5 L43.0,11.5 L42.5,13.3 L42.5,15.0 L43.0,15.7 L42.4,19.1 L42.4,24.6 L42.0,25.1 L42.0,27.5 L42.2,28.0 L42.8,28.3 L45.1,28.5 L45.3,29.2 L41.4,31.1 L40.9,31.6 L39.5,35.8 L39.0,39.8 L39.5,42.4 L40.3,45.1 L42.0,48.5 L45.9,50.4 L46.5,50.1 L47.4,50.3 L47.5,50.7 L46.9,51.2 L46.8,52.1 L48.0,57.3 L49.4,57.8 L49.8,58.2 L50.3,57.6 L51.8,57.3 L51.9,57.0 L51.9,53.9 L52.2,53.5 L52.3,50.7 L53.2,50.5 L56.4,49.0 L58.0,46.4 L59.5,41.6 L59.7,38.0 L59.2,34.3 L58.1,31.4 L56.4,30.4 L54.2,29.6 L54.1,29.3 L54.3,28.6 L55.4,28.6 L56.0,28.2 L56.2,27.5 L56.5,13.0 L56.3,11.8 L55.4,10.4 L53.4,9.2 Z';

function FormSilhouette({
  path,
  className,
}: {
  path: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={cn('text-white', className)}
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={4}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

const FORM_TILE_CLASS: Record<ProductForm, string> = {
  [ProductForm.STAND]:
    'bg-gradient-to-br from-teal-400 to-teal-700 shadow-md shadow-teal-950/40',
  [ProductForm.CARD]:
    'bg-gradient-to-br from-blue-400 to-blue-700 shadow-md shadow-blue-950/40',
  [ProductForm.BADGE]:
    'bg-gradient-to-br from-violet-400 to-violet-700 shadow-md shadow-violet-950/40',
};

export type PhysicalFormTileProps = {
  form?: ProductForm;
  label: string;
  className?: string;
  size?: 'sm' | 'md';
};

export function PhysicalFormTile({
  form,
  label,
  className,
  size = 'md',
}: PhysicalFormTileProps) {
  const sizeClass =
    size === 'sm' ? 'size-9 rounded-[10px]' : 'size-11 rounded-xl';
  const iconClass = size === 'sm' ? 'size-[22px]' : 'size-[26px]';

  if (!form) {
    return (
      <div
        role="img"
        aria-label={label}
        className={cn(
          'grid shrink-0 place-items-center',
          ghostAccentIconTileClass,
          sizeClass,
          className,
        )}
      >
        <Package className={iconClass} aria-hidden />
      </div>
    );
  }

  const path =
    form === ProductForm.STAND
      ? STAND_PATH
      : form === ProductForm.CARD
        ? CARD_PATH
        : BADGE_PATH;

  return (
    <div
      role="img"
      aria-label={label}
      className={cn(
        'grid shrink-0 place-items-center',
        sizeClass,
        FORM_TILE_CLASS[form],
        className,
      )}
    >
      <FormSilhouette path={path} className={iconClass} />
    </div>
  );
}
